import { createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { isValidDate } from '../../utils/validate';
import { cartCheckApiFromArrayOfObj, refreshApproveOrderItem } from '../../api/cart';

import {
  getAddToOrderListData,
  getOrdersUnreadData,
  getPendingOrderInfoData,
  getPendingOrdersData,
  getPreviousOrderInfoData,
  getPreviousOrderInfoDataByNumber,
  getPreviousOrdersData,
  getPreviousVendorOrdersData,
  postUpdateOrderGuidePrices,
  changeBudgetDate,
  saveManualBudgetItemData,
  updateOnBehalfOf,
  postUpdateOrderVisits,
} from '../../api/orders';
// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  cartChecked: false,
  firstTime: true,
  cartDialog: false,
  openErrorDialog: false,
  error: null,
  data: {
    pendingOrders: [],
    previousOrders: [],
  },
  orderDateRange: {
    startDate: null,
    endDate: null,
  },
  orderById: {},
  cartCheckItemsById: {},
  addToOrderListById: [],
  catalogReplacementList: [],
  tempOrderListById: {},
  unreadOrders: 0,
  selectedPendingOrders: [],
  orderSelectType: '',
  vendorOrders: [],
};

const normalizeAndSortOrderItems = (items = []) =>
  items
    .map((item) => ({ ...item, approved: item.deleted ? false : item.approved }))
    .sort((a, b) => a.deleted - b.deleted || (a.agoraSubcategory?.id ?? 0) - (b.agoraSubcategory?.id ?? 0));

const slice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    clearItemData(state) {
      state.orderById = {};
      state.cartCheckItemsById = {};
      state.addToOrderListById = {};
      state.tempOrderListById = {};
    },
    clearItemDataById(state, action) {
      const { id } = action.payload;
      if (state.orderById?.[id]) {
        state.orderById[id] = {};
      }
    },

    startCart(state) {
      state.cartDialog = true;
      state.cartChecked = true;
    },
    closeDialog(state) {
      state.openErrorDialog = false;
    },
    openDialog(state) {
      state.openErrorDialog = true;
    },
    removeCartChecked(state) {
      state.cartChecked = false;
    },
    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getPendingOrdersSuccess(state, action) {
      state.isLoading = false;
      state.data.pendingOrders = action.payload;
    },

    getPreviousOrdersSuccess(state, action) {
      state.isLoading = false;
      state.data.previousOrders = action.payload;
    },

    updateOrderDateRange(state, action) {
      state.orderDateRange = action.payload;
    },
    updateBudgetDateSuccess(state, action) {
      const { orderId, budgetDate } = action.payload;
      state.orderById[orderId].budgetDate = budgetDate;
    },
    saveManualBudgetItemDataSuccess(state, action) {
      const { orderId, budget } = action.payload;
      const { itemQuantityId, unitPrice, qty, shipping, tax, addedBy, dateAdded } = budget;

      state.isLoading = false;
      const { orderById } = state;
      const item = orderById[orderId]?.items.find((x) => x.id === itemQuantityId);
      item.budgetPrice = [unitPrice];
      item.budgetQty = [qty];
      item.budgetShipping = [shipping];
      item.budgetTax = [tax];
      item.budgetSource = 'MANUAL';
      item.budgetSourceData = [addedBy];
      item.budgetUpdatedAt = dateAdded;
    },

    getOrdersInfoSuccessNoCartCheck(state, action) {
      action.payload.items = normalizeAndSortOrderItems(action.payload.items);
      const { id } = action.payload;
      state.isLoading = false;
      state.orderById[id] = action.payload;
    },
    getOrdersInfoSuccess(state, action) {
      action.payload.items = normalizeAndSortOrderItems(action.payload.items);
      const { id } = action.payload;
      state.isLoading = false;
      state.orderById[id] = action.payload;
    },
    getRerunOrdersInfoSuccess(state, action) {
      const { id, items, vendorId } = action.payload;
      items.forEach((item) => {
        if (vendorId !== '0' && vendorId !== 'all') item.deleted = item.deleted || item.vendorId !== parseInt(vendorId, 10);
        if (item.deleted) {
          item.approved = false;
        }
      });
      state.isLoading = false;
      state.orderById[id] = action.payload;
    },

    getCompletedOrdersSuccess(state, action) {
      state.isLoading = false;
      const { orderId, data } = action.payload;
      state.orderById[orderId] = data;
    },

    getAddToOrderSuccess(state, action) {
      state.isLoading = false;
      const { orderId, data } = action.payload;
      state.addToOrderListById[orderId] = data;
    },

    getReplacementSuccess(state, action) {
      state.isLoading = false;
      state.catalogReplacementList = action.payload;
    },

    updateSelectedSuccess(state, action) {
      state.isLoading = false;
      state.selectedPendingOrders = action.payload;
    },

    updateOrderSelectTypeSuccess(state, action) {
      state.isLoading = false;
      state.orderSelectType = action.payload;
    },

    updateOrder(state, action) {
      const { orderId, id, field, value } = action.payload;
      state.orderById[orderId].unsavedChanges = true;
      const { orderById } = state;
      const order = orderById[orderId];
      const { items } = order;
      const item = items.find((item) => item.id === id);
      item[field] = value;
      state.orderById[orderId] = order;
      state.isLoading = false;
    },

    updateOrderAddress(state, action) {
      const { orderId, value } = action.payload;
      state.orderById[orderId].orderAddress = value;
      state.isLoading = false;
    },

    updateOrderItem(state, action) {
      const { orderId, item } = action.payload;
      const { orderById } = state;
      const order = orderById[orderId];
      state.orderById[orderId] = {
        ...order,
        items: order.items.map((oldItem) => (oldItem.id === item.id ? item : oldItem)),
      };
      state.isLoading = false;
    },

    addItemToOrder(state, action) {
      const { orderById } = state;
      const { item } = action.payload;
      state.orderById[item.orderId].unsavedChanges = true;
      const order = orderById[item.orderId];
      const { items } = order;
      const existingItemIndex = items?.findIndex((existing) => existing.id === item.id);

      if (existingItemIndex !== -1) {
        if (item.quantity > 0) {
          items[existingItemIndex] = { ...items[existingItemIndex], ...item };
        } else {
          items?.splice(existingItemIndex, 1);
        }
      } else if (item.quantity > 0) {
        items.push(item);
      }

      state.orderById[item.orderId].items = items;
      state.isLoading = false;
    },

    addArrayOfItems(state, action) {
      const { arrayOfItems, orderId } = action.payload;
      const { orderById } = state;
      const order = orderById[orderId];
      const { items } = order;

      arrayOfItems.forEach((item) => {
        const existingItemIndex = items?.findIndex((existing) => existing.id === item.id);

        if (existingItemIndex !== -1) {
          if (item.quantity > 0) {
            items[existingItemIndex] = { ...items[existingItemIndex], ...item };
          } else {
            items?.splice(existingItemIndex, 1);
          }
        } else if (item.quantity > 0) {
          items.push(item);
        }
      });

      state.orderById[orderId].items = items;
      state.orderById[orderId].unsavedChanges = true;
      state.isLoading = false;
    },

    removeItemFromCartCheck(state, action) {
      const { orderId, id } = action.payload;
      const { cartCheckItemsById } = state;
      const cartCheckItems = cartCheckItemsById[orderId];
      const existingItemIndex = cartCheckItems?.findIndex((existing) => existing.id === id);
      if (existingItemIndex !== -1) {
        cartCheckItems?.splice(existingItemIndex, 1);
      }
      state.cartCheckItemsById[orderId] = cartCheckItems;
      state.openErrorDialog = cartCheckItems?.some((item) => item.errors?.length > 0);
      state.isLoading = false;
    },

    addTempItemToOrder(state, action) {
      const { item } = action.payload;
      const { tempOrderListById } = state;
      const tempOrderList = tempOrderListById[item.orderId] || [];

      const existingItemIndex = tempOrderList?.findIndex(
        (existing) =>
          existing.id === item.id ||
          (existing.vendorItemId === item.vendorItemId && existing.vendorId === item.vendorId)
      );

      if (existingItemIndex !== -1) {
        if (item.quantity > 0) {
          tempOrderList[existingItemIndex] = { ...tempOrderList[existingItemIndex], ...item };
        } else {
          tempOrderList?.splice(existingItemIndex, 1);
        }
      } else if (item.quantity > 0) {
        tempOrderList.push(item);
      }
      state.tempOrderListById[item.orderId] = tempOrderList;
      state.isLoading = false;
    },

    clearTempItemData(state, action) {
      const { orderId } = action.payload;
      state.tempOrderListById[orderId] = [];
      state.isLoading = false;
    },

    updateMessageCount(state, action) {
      const { id } = action.payload;
      const { data } = state;
      const { pendingOrders, previousOrders } = data;
      const pendingOrderIndex = pendingOrders.findIndex((order) => order.id === id);
      const previousOrderIndex = previousOrders.findIndex((order) => order.id === id);
      if (pendingOrderIndex !== -1) {
        pendingOrders[pendingOrderIndex].messageCount = 1;
      }
      if (previousOrderIndex !== -1) {
        previousOrders[previousOrderIndex].messageCount = 1;
      }
      state.data.pendingOrders = pendingOrders;
      state.data.previousOrders = previousOrders;
      state.isLoading = false;
    },

    addLinkedTickets(state, action) {
      const { orderId, idToAdd } = action.payload;
      state.orderById[orderId].linkedTickets = [...state.orderById[orderId]?.linkedTickets, idToAdd];
      state.isLoading = false;
    },

    updateCatalogQty(state, action) {
      const { row } = action.payload;
      const { addToOrderListById } = state;
      const addToOrderList = addToOrderListById[row.orderId];
      const existingItemIndex = addToOrderList?.findIndex((existing) => existing.id === row.id);
      if (existingItemIndex !== -1) {
        addToOrderList[existingItemIndex] = { ...addToOrderList[existingItemIndex], ...row };
      }
      state.addToOrderListById[row.orderId] = addToOrderList;
      state.isLoading = false;
    },

    updateItemsWithPrice(state, action) {
      const { items, orderId } = action.payload;
      const { cartCheckItemsById } = state;
      const cartCheckItems = cartCheckItemsById[orderId];
      const { items: orderItems } = state.orderById[orderId];
      items.forEach((item) => {
        const existingItemIndex = cartCheckItems?.findIndex(
          (existing) => existing.id === item.id && existing.vendorItemId === item.vendorItemId
        );
        if (existingItemIndex !== -1) {
          cartCheckItems?.splice(existingItemIndex, 1);
        }
        const orderItemIndex = orderItems?.findIndex(
          (existing) => existing.id === item.id && existing.vendorItemId === item.vendorItemId
        );
        if (orderItemIndex !== -1) {
          orderItems[orderItemIndex] = { ...orderItems[orderItemIndex], price: item.updatedPrice };
        }
      });

      state.cartCheckItemsById[orderId] = cartCheckItems;
      state.openErrorDialog = cartCheckItems?.some((item) => item.errors?.length > 0);
      state.isLoading = false;
    },

    updateOrderComments(state, action) {
      const { orderById } = state;
      const { commentArray, orderId } = action.payload;
      const order = orderById[orderId];
      order.orderComments = commentArray;
      state.orderById[orderId] = order;
      state.isLoading = false;
    },

    updateCartWithVendorData(state, action) {
      const { data, orderId, errorDialogShouldOpen } = action.payload;
      const { items: incomingItems } = data;
      if (errorDialogShouldOpen) {
        const hasItemError = incomingItems.some((item) => item.errors && item.errors.length > 0);
        state.openErrorDialog = hasItemError;
      }

      state.cartCheckItemsById[orderId] = incomingItems;
      state.isLoading = false;
    },

    getUnreadOrdersCountSuccess(state, action) {
      const { unreadOrders } = action.payload;
      state.isLoading = false;
      state.unreadOrders = unreadOrders;
    },

    updateOrderReadStatusSuccess(state, action) {
      const { orderId, isRead, unreadOrders } = action.payload;
      const order = state.data.pendingOrders.find((pendingOrder) => pendingOrder.id === orderId);
      if (order) order.read = isRead;
      state.unreadOrders = unreadOrders;
    },

    removeGlobalNote(state, action) {
      const { orderId, vendorId } = action.payload;
      state.orderById[orderId].removeGlobalNoteFromVendors = [
        ...(state.orderById[orderId].removeGlobalNoteFromVendors ?? []),
        vendorId,
      ];
    },

    editOnBehalfOfSuccess(state, action) {
      const { orderId, personFor, personForEmail } = action.payload;
      state.orderById[orderId].onBehalfOfEmail = personForEmail;
      state.orderById[orderId].onBehalfOf = personFor;
    },

    updateReviewersSuccess(state, action) {
      const { orderId, reviewers } = action.payload;
      state.orderById[orderId].reviewers = reviewers;
    },

    getPreviousVendorOrdersSuccess(state, action) {
      state.isLoading = false;
      state.vendorOrders = action.payload?.vendorOrders ?? [];
    },
  },
});

// Reducer
export default slice.reducer;
// ----------------------------------------------------------------------

function normalizePreviousOrderDateRange(startDate, endDate) {
  const normalizedStartDate =
    startDate === null || isValidDate(startDate)
      ? new Date(startDate).toISOString().split('T')[0]
      : new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();

  const normalizedEndDate = isValidDate(endDate) ? new Date(endDate) : new Date();
  normalizedEndDate.setHours(23, 59, 0, 0);

  return {
    startDate: normalizedStartDate,
    endDate: encodeURIComponent(normalizedEndDate.toISOString()),
  };
}

export function getPendingOrders() {
  return async (dispatch) => {
    // const { data } = getState().orders;
    // if (data.pendingOrders.length > 0) {
    //   return;
    // }
    dispatch(slice.actions.clearItemData());
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPendingOrdersData();
      dispatch(slice.actions.getPendingOrdersSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getPreviousOrders({ startDate, endDate }) {
  const normalizedDates = normalizePreviousOrderDateRange(startDate, endDate);
  return async (dispatch) => {
    dispatch(slice.actions.clearItemData());
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPreviousOrdersData(normalizedDates);
      dispatch(slice.actions.getPreviousOrdersSuccess(response.data));
      dispatch(slice.actions.updateOrderDateRange(normalizedDates));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getPreviousVendorOrders({ startDate, endDate }) {
  const normalizedDates = normalizePreviousOrderDateRange(startDate, endDate);
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPreviousVendorOrdersData(normalizedDates);
      dispatch(slice.actions.getPreviousVendorOrdersSuccess(response.data));
      dispatch(slice.actions.updateOrderDateRange(normalizedDates));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getAddToOrderList(id, facilityId, categoryId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getAddToOrderListData(facilityId, categoryId);
      dispatch(slice.actions.getAddToOrderSuccess({ orderId: id, data: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getCatalogReplacementListData(facilityId, categoryId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getAddToOrderListData(facilityId, categoryId);
      dispatch(slice.actions.getReplacementSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateSelectedPendingOrders(orders) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updateSelectedSuccess(orders));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateOrderSelectType(type) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updateOrderSelectTypeSuccess(type));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function saveBudgetBreakdownData(id, itemQuantityId, unitPrice, shipping, tax, qty) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await saveManualBudgetItemData(itemQuantityId, unitPrice, shipping, tax, qty);
      dispatch(slice.actions.saveManualBudgetItemDataSuccess({ orderId: id, budget: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getPreviousOrderInfo(orderId, onOrderWasNotApproved = null, vendorId = null) {
  return async (dispatch) => {
    await dispatch(slice.actions.clearItemDataById({ id: orderId }));
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPreviousOrderInfoData(orderId, vendorId);
      if (onOrderWasNotApproved && response.data && !response.data.approved) {
        onOrderWasNotApproved();
        dispatch(slice.actions.hasError({ message: 'Not approved yet' }));
        return;
      }
      dispatch(slice.actions.getCompletedOrdersSuccess({ orderId, data: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeCartCheckedFromRedux() {
  return async (dispatch) => {
    dispatch(slice.actions.removeCartChecked());
  };
}

export function getPreviousOrderInfoByNumber(orderId) {
  return async (dispatch) => {
    await dispatch(slice.actions.clearItemDataById({ id: orderId }));
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPreviousOrderInfoDataByNumber(orderId);
      dispatch(slice.actions.getCompletedOrdersSuccess({ orderId: response.data.id, data: response.data }));
      return response.data.id;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
    return null;
  };
}

export function getPendingOrderInfo(orderId, skipCartCheck = false, orderWasAlreadyApproved = null) {
  return async (dispatch) => {
    // await dispatch(slice.actions.clearItemDataById({ id: orderId }));
    dispatch(slice.actions.startLoading());
    try {
      const shortCircuitIfApproved = !!orderWasAlreadyApproved;
      const response = await getPendingOrderInfoData(orderId, shortCircuitIfApproved);
      if (response.status === 200) {
        if (response.data?.approved && shortCircuitIfApproved) {
          orderWasAlreadyApproved();
          dispatch(slice.actions.hasError({ message: 'Already approved' }));
          return;
        }
        if (skipCartCheck) dispatch(slice.actions.getOrdersInfoSuccessNoCartCheck(response.data));
        else dispatch(slice.actions.getOrdersInfoSuccess(response.data));
        if (response.data?.firstVisit) dispatch(getUnreadOrderCount());
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getRerunOrderInfo(orderId, vendorId) {
  return async (dispatch) => {
    await dispatch(slice.actions.clearItemDataById({ id: orderId }));
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPendingOrderInfoData(orderId, false);
      if (response.status === 200) {
        dispatch(slice.actions.getRerunOrdersInfoSuccess({ ...response.data, vendorId }));
        dispatch(getUnreadOrderCount());
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cartCheckWithData(orderId, facilityId, data, errorDialogShouldOpen = true) {
  const approvedItems = data?.filter((item) => item.approved && !item.deleted);

  return async (dispatch, getState) => {
    try {
      const response = await cartCheckApiFromArrayOfObj({ carts: approvedItems, facilityId });
      dispatch(slice.actions.updateCartWithVendorData({ data: response.data, orderId, errorDialogShouldOpen }));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return true;
    }
  };
}

export function updateOrderAddressInRedux(orderId, value) {
  return async (dispatch) => {
    dispatch(slice.actions.updateOrderAddress({ orderId, value }));
  };
}

export function updateOrderItemInRedux(orderId, id, field, value) {
  return async (dispatch) => {
    dispatch(slice.actions.updateOrder({ orderId, id, field, value }));
  };
}

export function updateOrderItemFullInRedux(orderId, id) {
  return async (dispatch, getState) => {
    try {
      const response = await refreshApproveOrderItem({ orderId, itemQuantityId: id });
      if (response.data) dispatch(slice.actions.updateOrderItem({ item: response.data, orderId }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeItemFromErrorList(orderId, id) {
  return async (dispatch) => {
    dispatch(slice.actions.removeItemFromCartCheck({ orderId, id }));
  };
}

export function addItemToAnOrderInRedux(item) {
  return async (dispatch) => {
    dispatch(slice.actions.addItemToOrder({ item }));
  };
}

export function addItemToAnOrderFromCatalog(data, category, orderId) {
  return async (dispatch) => {
    if (!data || !data.productDetails?.length) return;
    const item = data.productDetails[0];
    const productDetailUOMs = item.productDetailUOM
      ? [{ price: item.price, uomType: item.uomType, uomAmount: item.uomAmount }, item.productDetailUOM]
      : null;
    dispatch(
      slice.actions.addItemToOrder({
        item: {
          ...item,
          ...category,
          productDetailUOMs,
          description: data.description,
          notes: data.notes,
          orderId,
          id: item.vendorItemId,
          approved: true,
          unsavedId: 0,
          itemId: item.id,
          quantity: 1,
        },
      })
    );
  };
}

export function updatePrice(orderId, facilityId, items) {
  return async (dispatch, getState) => {
    try {
      const catalogItems = items.filter((item) => item.itemId > 0);
      if (catalogItems.length > 0) await postUpdateOrderGuidePrices({ facilityId, items: catalogItems });
      dispatch(slice.actions.updateItemsWithPrice({ orderId, items }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateItemPrice(orderId, item) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.updateItemsWithPrice({ orderId, items: [item] }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addItemsToAnOrderInRedux({ orderId, arrayOfItems }) {
  return async (dispatch) => {
    dispatch(slice.actions.addArrayOfItems({ orderId, arrayOfItems }));
  };
}
export function addTempItemToAnOrderInRedux(item) {
  return async (dispatch) => {
    dispatch(slice.actions.addTempItemToOrder({ item }));
  };
}
export function updateCatalogQtyInRedux(row) {
  return async (dispatch) => {
    dispatch(slice.actions.updateCatalogQty({ row }));
  };
}

export function updateOrderCommentsInRedux({ orderId, commentArray }) {
  return async (dispatch) => {
    dispatch(slice.actions.updateOrderComments({ orderId, commentArray }));
  };
}

export function clearTempOrderList(id) {
  return async (dispatch) => {
    dispatch(slice.actions.clearTempItemData({ orderId: id }));
  };
}
export function clearOrderState() {
  return async (dispatch) => {
    dispatch(slice.actions.clearItemData());
  };
}

export function addLinkedTicketsToOrder({ orderId, idToAdd }) {
  return async (dispatch) => {
    dispatch(slice.actions.addLinkedTickets({ orderId, idToAdd }));
  };
}

export function updateMessageCountById(id) {
  return async (dispatch) => {
    dispatch(slice.actions.updateMessageCount({ id }));
  };
}

export function getUnreadOrderCount() {
  return async (dispatch) => {
    try {
      const response = await getOrdersUnreadData();
      dispatch(slice.actions.getUnreadOrdersCountSuccess({ unreadOrders: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markAsRead(orderId, isRead) {
  return async (dispatch) => {
    try {
      const response = await postUpdateOrderVisits(orderId, isRead);
      dispatch(
        slice.actions.updateOrderReadStatusSuccess({
          orderId,
          isRead,
          unreadOrders: response.data,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setUnreadOrderCount(unreadOrderCount) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getUnreadOrdersCountSuccess({ unreadOrders: unreadOrderCount }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateBudgetDate(id, budgetDate) {
  return async (dispatch) => {
    try {
      const budgetDateIso = budgetDate.toISOString();
      const response = await changeBudgetDate(id, budgetDateIso);
      if (response.status === 200 && response.data === true) {
        dispatch(slice.actions.updateBudgetDateSuccess({ orderId: id, budgetDate }));
        return response.data;
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      enqueueSnackbar('Failed to update budget date.', { variant: 'error' });
    }
    return null;
  };
}

export function closeErrorDialog() {
  return async (dispatch) => {
    dispatch(slice.actions.closeDialog());
  };
}

export function openErrorDialog() {
  return async (dispatch) => {
    dispatch(slice.actions.openDialog());
  };
}

export function removeGlobalNote(orderId, vendorId) {
  return async (dispatch) => {
    dispatch(slice.actions.removeGlobalNote({ orderId, vendorId }));
  };
}

export function editOnBehalfOf(orderId, onBehalfOfEmail) {
  return async (dispatch) => {
    try {
      const response = await updateOnBehalfOf(orderId, onBehalfOfEmail);
      if (response.status === 200) {
        const { personFor, personForEmail } = response.data;
        dispatch(slice.actions.editOnBehalfOfSuccess({ orderId, personFor, personForEmail }));
        return response.data;
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
    return null;
  };
}
export function updateReviewersInRedux(orderId, reviewers) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateReviewersSuccess({ orderId, reviewers }));
      return reviewers;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
    return null;
  };
}
